import { Link } from 'react-router-dom';
import { benefits, comparisonData, resources, seoArticles } from '@/mocks/seoData';

export default function ContentSections() {
  return (
    <>
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            <div className="lg:w-1/2">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-100 mb-6">
                <i className="ri-information-line text-2xl text-accent-600"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                What Is BaseTao Spreadsheet?
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  <strong className="text-foreground-800">BaseTao Spreadsheet</strong> is a sophisticated yet user-friendly research database system that transforms how shoppers organize and analyze product information. Unlike traditional browsing methods that rely on scattered browser tabs and handwritten notes, the BaseTao Spreadsheet provides a centralized platform where every piece of product data lives in one structured location.
                </p>
                <p>
                  The concept behind <strong className="text-foreground-800">BaseTao Spreadsheet</strong> emerged from the real challenges faced by international shoppers who needed a better way to track hundreds of products across multiple categories — from <strong className="text-foreground-800">shoes and hoodies</strong> to <strong className="text-foreground-800">accessories and bags</strong>. By consolidating product URLs, pricing information, shipping estimates, seller ratings, and discount codes into organized columns and rows, users gain a complete overview of their potential purchases at a single glance.
                </p>
                <p>
                  What sets <strong className="text-foreground-800">BaseTao Spreadsheet</strong> apart is its specialized focus on the shopping workflow. Every template field and organizational structure has been designed specifically for product research — from tracking warehouse storage times to comparing shipping carriers and calculating total landed costs. This focused approach means you spend less time organizing data and more time making confident purchasing decisions.
                </p>
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20workspace%20with%20dual%20monitors%20showing%20productivity%20dashboards%20and%20organized%20desk%20accessories%2C%20bright%20modern%20office%20with%20large%20windows%20and%20natural%20daylight%2C%20clean%20minimalist%20aesthetic%2C%20high%20detail%20corporate%20photography%2C%20warm%20wood%20desk%20texture%2C%20productive%20atmosphere&width=800&height=600&seq=what-is-v2-section&orientation=landscape"
                alt="Organized product research workspace with BaseTao Spreadsheet"
                className="w-full rounded-2xl object-cover"
                style={{ aspectRatio: '4/3' }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Why Use BaseTao Spreadsheet?
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Six powerful features that transform your shopping research workflow
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="group p-6 rounded-2xl bg-white border border-gray-200 hover:border-accent-200 hover:shadow-md transition-all duration-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:scale-105 transition-transform">
                  <i className={`${benefit.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              BaseTao Spreadsheet vs Traditional Research
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              See how the BaseTao Spreadsheet transforms your research workflow
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-4 font-semibold text-gray-800">Feature</th>
                  <th className="text-left px-6 py-4 font-semibold text-accent-700">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-star-fill text-xs"></i>
                      BaseTao Spreadsheet
                    </span>
                  </th>
                  <th className="text-left px-6 py-4 font-semibold text-gray-500">Traditional Research</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={row.feature} className={`border-t border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-6 py-3.5 text-gray-700 font-medium">{row.feature}</td>
                    <td className="px-6 py-3.5 text-accent-700">{row.basetao}</td>
                    <td className="px-6 py-3.5 text-gray-500">{row.traditional}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Latest BaseTao Resources
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Explore our comprehensive guides and tools
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((resource) => (
              <Link
                key={resource.title}
                to={`/article/${resource.slug}`}
                className="group rounded-2xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: '16/10' }}>
                  <img
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{resource.description}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 group-hover:gap-2 transition-all">
                    Read More
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              In-Depth SEO Guides &amp; Resources
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Dive deeper with our comprehensive BaseTao guides — each packed with original research, data tables, and actionable strategies
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {seoArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group rounded-2xl bg-white border border-gray-200 hover:border-accent-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: '16/10' }}>
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-sm font-semibold text-gray-900 mb-2 group-hover:text-accent-700 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed mb-3 line-clamp-2">{article.description}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-600 group-hover:gap-2 transition-all">
                    Read Full Guide
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20cybersecurity%20dashboard%20displayed%20on%20a%20sleek%20laptop%20screen%20with%20shield%20icons%20and%20green%20status%20indicators%2C%20modern%20office%20setting%20with%20natural%20lighting%2C%20trust%20and%20protection%20concept%2C%20clean%20corporate%20photography%2C%20bright%20professional%20atmosphere&width=800&height=600&seq=safety-v2-section&orientation=landscape"
                alt="Safe and secure BaseTao Spreadsheet research"
                className="w-full rounded-2xl object-cover"
                style={{ aspectRatio: '4/3' }}
              />
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-100 mb-6">
                <i className="ri-shield-check-line text-2xl text-accent-600"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Safety &amp; Trust
              </h2>
              <div className="space-y-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  When using <strong className="text-foreground-800">BaseTao Spreadsheet</strong> for your product research, we prioritize transparency and accuracy. Our resource database is regularly reviewed to ensure the information you access remains reliable and up to date.
                </p>
                <p>
                  Always verify seller information through multiple sources before making purchases. The <strong className="text-foreground-800">BaseTao Spreadsheet</strong> provides structured comparison tools, but we recommend cross-referencing seller ratings and product reviews independently for the most informed decisions.
                </p>
                <p>
                  Protect your personal information by only sharing necessary details during transactions. The <strong className="text-foreground-800">BaseTao Spreadsheet</strong> system helps you track and organize product data without requiring sensitive personal or payment information — your privacy and security remain fully in your control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}